import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Line, Stars } from "@react-three/drei";
import * as THREE from "three";
import { CanvasInView } from "./Reveal";

export const NIGERIA = { lat: 9.06, lng: 7.49 };

export const GLOBAL_HUBS = [
  { lat: 51.9, lng: 4.5 },
  { lat: 29.7, lng: -95.3 },
  { lat: 1.35, lng: 103.8 },
  { lat: 25.2, lng: 55.3 },
  { lat: 51.5, lng: -0.12 },
  { lat: 43.65, lng: -79.38 },
  { lat: 19.07, lng: 72.87 },
];

export const REGIONAL_HUBS = [
  { lat: 5.6, lng: -0.2 },
  { lat: -26.2, lng: 28.0 },
  { lat: -1.29, lng: 36.8 },
  { lat: 25.2, lng: 55.3 },
  { lat: 51.9, lng: 4.5 },
  { lat: 43.65, lng: -79.38 },
];

export const TORONTO = { lat: 43.65, lng: -79.38 };

export function latLngToVec3(lat, lng, r) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

function DotSphere({ radius, count, color }) {
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i++) {
      const y = 1 - (i / (count - 1)) * 2;
      const rad = Math.sqrt(1 - y * y);
      const theta = golden * i;
      arr[i * 3] = Math.cos(theta) * rad * radius;
      arr[i * 3 + 1] = y * radius;
      arr[i * 3 + 2] = Math.sin(theta) * rad * radius;
    }
    return arr;
  }, [count, radius]);
  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.03} color={color} transparent opacity={0.9} sizeAttenuation depthWrite={false} />
    </points>
  );
}

function Atmosphere({ radius }) {
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: { c: { value: new THREE.Vector3(0.82, 0.6, 0.24) } },
        vertexShader:
          "varying vec3 vNormal; void main(){ vNormal = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
        fragmentShader:
          "uniform vec3 c; varying vec3 vNormal; void main(){ float intensity = pow(0.68 - dot(vNormal, vec3(0.0,0.0,1.0)), 3.2); gl_FragColor = vec4(c, 1.0) * intensity * 0.85; }",
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
      }),
    []
  );
  return (
    <mesh material={material}>
      <sphereGeometry args={[radius, 48, 48]} />
    </mesh>
  );
}

function Arc({ from, to, color, radius }) {
  const lineRef = useRef();
  const points = useMemo(() => {
    const s = latLngToVec3(from.lat, from.lng, radius * 1.001);
    const e = latLngToVec3(to.lat, to.lng, radius * 1.001);
    const dist = s.distanceTo(e);
    const mid = s
      .clone()
      .add(e)
      .multiplyScalar(0.5)
      .normalize()
      .multiplyScalar(radius * (1 + dist * 0.16));
    return new THREE.QuadraticBezierCurve3(s, mid, e).getPoints(72);
  }, [from, to, radius]);
  useFrame((_, delta) => {
    if (lineRef.current) lineRef.current.material.dashOffset -= delta * 0.5;
  });
  return (
    <Line
      ref={lineRef}
      points={points}
      color={color}
      lineWidth={1.6}
      dashed
      dashSize={0.28}
      gapSize={0.16}
      transparent
      opacity={1}
    />
  );
}

function HubDot({ lat, lng, radius }) {
  const pos = useMemo(() => latLngToVec3(lat, lng, radius * 1.002), [lat, lng, radius]);
  return (
    <mesh position={pos}>
      <sphereGeometry args={[0.034, 12, 12]} />
      <meshBasicMaterial color="#cfe9f2" />
    </mesh>
  );
}

function PulseMarker({ lat, lng, radius }) {
  const ring = useRef();
  const { pos, quat } = useMemo(() => {
    const p = latLngToVec3(lat, lng, radius * 1.004);
    const q = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      p.clone().normalize()
    );
    return { pos: p, quat: q };
  }, [lat, lng, radius]);
  useFrame(({ clock }) => {
    const t = (clock.elapsedTime % 2.2) / 2.2;
    if (ring.current) {
      ring.current.scale.setScalar(0.5 + t * 1.8);
      ring.current.material.opacity = 0.7 * (1 - t);
    }
  });
  return (
    <group position={pos} quaternion={quat}>
      <mesh>
        <sphereGeometry args={[0.045, 16, 16]} />
        <meshBasicMaterial color="#F0C05A" />
      </mesh>
      <mesh ref={ring}>
        <ringGeometry args={[0.06, 0.075, 40]} />
        <meshBasicMaterial color="#F0C05A" transparent side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
    </group>
  );
}

function GlobeScene({ radius, hubs, dotCount }) {
  const group = useRef();
  const baseY = THREE.MathUtils.degToRad(-(90 + NIGERIA.lng));
  const baseX = THREE.MathUtils.degToRad(NIGERIA.lat * 0.9);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (group.current) {
      group.current.rotation.y = baseY + Math.sin(t * 0.1) * 0.18;
      group.current.rotation.x = baseX + Math.cos(t * 0.07) * 0.05;
    }
  });
  return (
    <>
      <ambientLight intensity={0.35} />
      <pointLight position={[6, 4, 6]} intensity={30} color="#F0C05A" />
      <pointLight position={[-6, -3, 4]} intensity={14} color="#00E5FF" />
      <group ref={group}>
        <mesh>
          <sphereGeometry args={[radius * 0.985, 64, 64]} />
          <meshBasicMaterial color="#070d18" />
        </mesh>
        <DotSphere radius={radius} count={dotCount} color="#7fa8c4" />
        <Atmosphere radius={radius * 1.22} />
        {hubs.map((h, i) => (
          <Arc key={`a-${i}`} from={h} to={NIGERIA} color={i % 2 ? "#00E5FF" : "#E8B45A"} radius={radius} />
        ))}
        {hubs.map((h, i) => (
          <HubDot key={`h-${i}`} lat={h.lat} lng={h.lng} radius={radius} />
        ))}
        <PulseMarker lat={NIGERIA.lat} lng={NIGERIA.lng} radius={radius} />
      </group>
      <Stars radius={40} depth={30} count={1400} factor={2.4} saturation={0} fade speed={0.6} />
    </>
  );
}

export default function Globe({ className = "", hubs = GLOBAL_HUBS, dotCount, offsetX }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const count = dotCount ?? (isMobile ? 700 : 1600);
  const shift = offsetX ?? (isDesktop ? 0.9 : 0);
  return (
    <CanvasInView
      className={className}
      camera={{ position: [0, 0.5, 6.8], fov: 42 }}
      dpr={[1, 1.75]}
    >
      <group position={[shift, 0.25, 0]}>
        <GlobeScene radius={2} hubs={hubs} dotCount={count} />
      </group>
    </CanvasInView>
  );
}
